import { useEffect, useState } from 'react';

import { useMaxWidth } from '@grupoboticario/flora-react';
import {
  ArrowRightIcon,
  CheckSingleIcon,
  ExclamationMarkHexagonIcon,
  ExclamationMarkTriangleOutlineIcon,
} from '@grupoboticario/flora-react-icons';
import { checkout } from 'core';
import { actions } from 'core/use-cases/checkout';
import { useCommand } from 'core/use-cases/utilities';
import { useNavigate } from 'react-router';
import { Layout, Text } from 'ui/components/primitives';

import { GiftsHeader } from './widgets';
import { GiftsContentHeader, GiftsCarrousel } from './widgets/components';
import { GiftStyles } from './widgets/Gifts.styles';

const Gifts = () => {
  const navigate = useNavigate();
  const {
    rewards: { data: { choose = [] } = {} },
    selectedPromotions = [],
  } = checkout.hooks.useCheckout();
  const isMobile = useMaxWidth('992px');
  const { dispatchCommand } = useCommand();
  const [maxQuantity, setMaxQuantity] = useState(0);
  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    dispatchCommand(actions.selectedGiftsCreate());
    navigate('/checkout/brindes');
  }, []);

  useEffect(() => {
    if (choose.length) {
      const maxGiftQuantity = choose
        .flatMap((promotion) => promotion.giftQuantity)
        .reduce((sum, quantity) => sum + quantity);
      setMaxQuantity(maxGiftQuantity);
    }
  }, [choose]);

  useEffect(() => {
    const selectedQuantity = selectedPromotions
      .flatMap((promotion) => promotion?.rewards)
      .reduce((sum, reward) => sum + (reward?.quantity || 0), 0);

    selectedQuantity === maxQuantity ? setCanSubmit(true) : setCanSubmit(false);
  }, [selectedPromotions]);

  return (
    <div data-testid="gifts-container">
      <GiftsHeader />
      <GiftsContentHeader />
      <Layout>
        <GiftStyles.GiftsContainer marginBottom="sp-0" marginTop="sp-6">
          <GiftStyles.Options>
            {choose.map((gift, index) => {
              const alreadySelectedPromotion = selectedPromotions[index];

              const selectedAmount =
                alreadySelectedPromotion?.rewards?.reduce(
                  (sum, { quantity }) => sum + quantity,
                  0,
                ) || 0;

              const isCompleted = selectedAmount === gift.giftQuantity;
              const canAddItem = selectedAmount >= gift.giftQuantity;
              const promotionNumber = index + 1;

              return (
                <div key={index}>
                  <GiftStyles.Skus data-testid={`promotion-${index}`}>
                    <GiftStyles.CardHeader.Container
                      data-next-order-allowed={gift.nextOrderAllowed}
                    >
                      <GiftStyles.CardHeader.Title>
                        Promoção{' '}
                        {String(index).length === 1 ? `0${promotionNumber}` : promotionNumber}
                      </GiftStyles.CardHeader.Title>
                      <GiftStyles.Tag data-completed={isCompleted}>
                        {isCompleted ? (
                          <CheckSingleIcon size={12} />
                        ) : (
                          <ExclamationMarkHexagonIcon size={12} />
                        )}
                        <Text variant="auxiliar-small">
                          Você escolheu {selectedAmount} de {gift.giftQuantity}
                        </Text>
                      </GiftStyles.Tag>
                    </GiftStyles.CardHeader.Container>
                    {gift.nextOrderAllowed && (
                      <GiftStyles.CardHeader.NextOrderAllowed>
                        <ExclamationMarkTriangleOutlineIcon size={isMobile ? 12 : 32} />
                        <GiftStyles.CardHeader.NextOrderAllowedText variant="body-medium-short">
                          Por conta do estoque, alguns itens desta promoção podem ser enviados em
                          compras futuras
                        </GiftStyles.CardHeader.NextOrderAllowedText>
                      </GiftStyles.CardHeader.NextOrderAllowed>
                    )}
                    <GiftsCarrousel
                      gift={gift}
                      isCompleted={isCompleted}
                      canAddItem={canAddItem}
                      isMobile={!!isMobile}
                      alreadySelectedPromotion={alreadySelectedPromotion}
                      index={index}
                    />
                  </GiftStyles.Skus>
                </div>
              );
            })}
          </GiftStyles.Options>
          <GiftStyles.SubmitButton
            disabled={!canSubmit}
            onClick={() =>
              dispatchCommand(actions.promotionFetch.request(selectedPromotions || []))
            }
          >
            <span>Continuar para pagamento</span>
            <ArrowRightIcon />
          </GiftStyles.SubmitButton>
        </GiftStyles.GiftsContainer>
      </Layout>
    </div>
  );
};

export default Gifts;
